import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ClipboardCheck, Eye, XCircle } from "lucide-react";
import * as React from "react";

import { ApiError } from "@/api/client";
import { serviceReview } from "@/api/endpoints";
import type { ReviewService, ServiceReviewStatus } from "@/api/types";
import { Field, FilterBar, PageHeader, Pagination } from "@/components/PageHeader";
import { Badge, toneFor } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  EmptyState,
  ErrorState,
  FormError,
  Spinner,
  TableSkeleton,
} from "@/components/ui/feedback";
import { Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { formatCurrency, formatDateTime, humanize, relativeTime } from "@/lib/utils";

const STATUS_OPTIONS = [
  { label: "Awaiting review", value: "PENDING" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
];

function price(s: ReviewService): string {
  return `${formatCurrency(s.base_price)} / ${humanize(s.rate_unit).toLowerCase()}`;
}

export default function ServiceReviewPage() {
  const [page, setPage] = React.useState(1);
  const [status, setStatus] = React.useState("PENDING");
  const [open, setOpen] = React.useState<ReviewService | null>(null);

  React.useEffect(() => setPage(1), [status]);

  const query = useQuery({
    queryKey: ["service-review", { page, status }],
    queryFn: () =>
      serviceReview.queue({
        page,
        page_size: 20,
        review_status: status as ServiceReviewStatus,
      }),
  });

  return (
    <>
      <PageHeader
        title="Service review"
        description="Services providers have published. Farmers see a service only after it is approved here; changing its title, description, category or equipment sends it back."
      />

      <FilterBar>
        <div className="w-52">
          <Select
            value={status}
            onValueChange={setStatus}
            options={STATUS_OPTIONS}
            placeholder="Awaiting review"
            aria-label="Filter by review status"
          />
        </div>
      </FilterBar>

      <Card className="overflow-hidden">
        {query.isLoading ? (
          <TableSkeleton cols={6} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data && query.data.items.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.items.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.title}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {s.provider?.business_name ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {s.category_name ?? "—"}
                    </TableCell>
                    <TableCell className="tabular">{price(s)}</TableCell>
                    <TableCell className="tabular text-muted-foreground">
                      {relativeTime(s.submitted_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => setOpen(s)}>
                        <Eye />
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              page={query.data.page}
              pageSize={query.data.pageSize}
              total={query.data.total}
              onPageChange={setPage}
            />
          </>
        ) : (
          <EmptyState
            icon={ClipboardCheck}
            title={status === "PENDING" ? "Review queue is clear" : "Nothing here"}
            description={
              status === "PENDING"
                ? "No services are waiting for approval."
                : `No ${humanize(status).toLowerCase()} services.`
            }
          />
        )}
      </Card>

      {open && <ReviewDrawer service={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function ReviewDrawer({
  service,
  onClose,
}: {
  service: ReviewService;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const review = useMutation({
    mutationFn: ({ approve }: { approve: boolean }) =>
      serviceReview.review(service.id, approve, reason || undefined),
    onSuccess: (_data, variables) => {
      toast.success(
        variables.approve ? "Service approved" : "Service rejected",
        service.title,
      );
      void queryClient.invalidateQueries({ queryKey: ["service-review"] });
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not submit the review."),
  });

  function submit(approve: boolean) {
    setError(null);
    // Mirrors the server rule so the reviewer finds out before the round trip.
    if (!approve && !reason.trim()) {
      setError("A rejection reason is required — the provider sees this text.");
      return;
    }
    review.mutate({ approve });
  }

  const provider = service.provider;
  const approved = service.review_status === "APPROVED";

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent side="right">
        <DialogHeader>
          <DialogTitle>{service.title}</DialogTitle>
          <DialogDescription>
            {approved
              ? "Live for farmers. Rejecting takes it down; bookings already made carry on."
              : "Not visible to farmers until approved."}
          </DialogDescription>
        </DialogHeader>

        <dl className="grid grid-cols-2 gap-4">
          <Field label="Status">
            <Badge variant={toneFor(service.review_status)}>
              {humanize(service.review_status)}
            </Badge>
          </Field>
          <Field label="Category">{service.category_name ?? "—"}</Field>
          <Field label="Price">
            <span className="tabular">{price(service)}</span>
          </Field>
          <Field label="Pricing">{humanize(service.pricing_type)}</Field>
          <Field label="Submitted">{formatDateTime(service.submitted_at)}</Field>
          <Field label="Provider switch">{service.is_active ? "On" : "Off"}</Field>
        </dl>

        <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          What the farmer reads
        </h3>
        <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-4 text-sm">
          <p className="whitespace-pre-wrap">
            {service.description || (
              <span className="text-muted-foreground">No description.</span>
            )}
          </p>
          {service.equipment_tags?.length ? (
            <div className="flex flex-wrap gap-1.5">
              {service.equipment_tags.map((t) => (
                <Badge key={t}>{t}</Badge>
              ))}
            </div>
          ) : null}
        </div>

        <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Provider
        </h3>
        <dl className="grid grid-cols-2 gap-4">
          <Field label="Business">{provider?.business_name ?? "—"}</Field>
          <Field label="Phone">
            <span className="tabular">{provider?.phone ?? "—"}</span>
          </Field>
          <Field label="Location">
            {[provider?.city, provider?.state].filter(Boolean).join(", ") || "—"}
          </Field>
          <Field label="KYC">
            {provider ? (
              <Badge variant={toneFor(provider.verification_status)}>
                {humanize(provider.verification_status)}
              </Badge>
            ) : (
              "—"
            )}
          </Field>
        </dl>

        {service.review_note && (
          <p className="mt-4 text-xs" style={{ color: "var(--status-critical)" }}>
            Last rejected: {service.review_note}
          </p>
        )}

        <div className="mt-6 space-y-2">
          <Label htmlFor="service-reason">
            Reason{" "}
            <span className="text-muted-foreground">
              (required to reject — the provider sees this)
            </span>
          </Label>
          <Textarea
            id="service-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Description doesn't match the category — this is harvesting, not tilling"
          />
          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="destructive"
            disabled={review.isPending || service.review_status === "REJECTED"}
            onClick={() => submit(false)}
          >
            <XCircle />
            Reject
          </Button>
          {!approved && (
            <Button disabled={review.isPending} onClick={() => submit(true)}>
              {review.isPending ? <Spinner /> : <CheckCircle2 />}
              Approve
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
